import { useState, useEffect, useCallback } from 'react';
import MaybeLayout from '../../components/layout/MaybeLayout';
import { api } from '../../api/api';
import DevicesOtherIcon from '@mui/icons-material/DevicesOther';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import VpnLockIcon from '@mui/icons-material/VpnLock';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SystemUpdateIcon from '@mui/icons-material/SystemUpdate';
import TerminalIcon from '@mui/icons-material/Terminal';
import HistoryIcon from '@mui/icons-material/History';
import CloseIcon from '@mui/icons-material/Close';
import './devicehub.css';
import { COMMANDS, toCommandBody } from './deviceCommands';
import { Link } from 'react-router-dom';

function StatBox({ icon, label, value, color }) {
  const bg = color === '#551FFF' ? 'rgba(85,31,255,0.09)'
    : color === '#22c55e' ? 'rgba(34,197,94,0.1)'
    : color === '#ef4444' ? 'rgba(239,68,68,0.1)'
    : color === '#3b82f6' ? 'rgba(59,130,246,0.1)'
    : 'rgba(245,158,11,0.12)';
  return (
    <div className="dh-stat-box">
      <div className="dh-stat-box__icon" style={{ background: bg, color }}>{icon}</div>
      <div>
        <div className="dh-stat-box__value" style={{ color }}>{value}</div>
        <div className="dh-stat-box__label">{label}</div>
      </div>
    </div>
  );
}

function DeviceHub({ user, embedded = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cmdDialog, setCmdDialog] = useState(null); // { deviceCode, name }
  const [cmdType, setCmdType] = useState('restart_app');
  const [history, setHistory] = useState(null); // { deviceCode, commands }
  const [sending, setSending] = useState(false);
  const [filter, setFilter] = useState('all'); // all, online, offline

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/peer/device-hub');
      setData(res.data);
    } catch (e) {
      console.error('DeviceHub fetch error:', e);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Quick command (no dialog)
  const quickCmd = async (deviceCode, command) => {
    try {
      await api.post('/peer/command/send', { deviceCode, command });
      fetchData();
    } catch (e) {
      alert(`Command failed: ${e.message}`);
    }
  };

  // Send command from dialog
  const sendCommand = async () => {
    if (!cmdDialog) return;
    setSending(true);
    try {
      await api.post('/peer/command/send', { deviceCode: cmdDialog.deviceCode, ...toCommandBody(cmdType) });
      setCmdDialog(null);
      fetchData();
    } catch (e) {
      alert(`Command failed: ${e.message}`);
    }
    setSending(false);
  };

  // Load command history for a device
  const loadHistory = async (deviceCode) => {
    try {
      const res = await api.get(`/peer/command/history/${deviceCode}?limit=20`);
      setHistory({ deviceCode, commands: res.data.commands || [] });
    } catch (e) {
      console.error('History fetch error:', e);
    }
  };

  const filteredDevices = data?.devices?.filter(d => {
    if (filter === 'online') return d.status.socket === 'online';
    if (filter === 'offline') return d.status.socket === 'offline';
    return true;
  }) || [];

  const fmtTime = (ts) => {
    if (!ts) return '-';
    const d = new Date(ts);
    const now = new Date();
    const diffMs = now - d;
    if (diffMs < 60000) return 'Just now';
    if (diffMs < 3600000) return `${Math.floor(diffMs / 60000)}m ago`;
    if (diffMs < 86400000) return `${Math.floor(diffMs / 3600000)}h ago`;
    return d.toLocaleDateString();
  };

  const statusBadge = (status) => {
    if (status === 'online') return <span className="dh-badge dh-badge--online">Online</span>;
    return <span className="dh-badge dh-badge--offline">Offline</span>;
  };

  const wgBadge = (wg) => {
    if (wg === 'connected') return <span className="dh-badge dh-badge--wg">WG</span>;
    if (wg === 'disconnected') return <span className="dh-badge dh-badge--offline">WG Off</span>;
    return null;
  };

  const cmdStatusBadge = (status) => {
    if (status === 'acked') return <span className="dh-badge dh-badge--online">Acked</span>;
    if (status === 'delivered') return <span className="dh-badge dh-badge--wg">Delivered</span>;
    if (status === 'pending') return <span className="dh-badge dh-badge--pending">Pending</span>;
    if (status === 'failed') return <span className="dh-badge dh-badge--offline">Failed</span>;
    if (status === 'expired') return <span className="dh-badge dh-badge--offline">Expired</span>;
    return <span className="dh-badge">{status}</span>;
  };

  return (
    <MaybeLayout embedded={embedded} title="Device Hub" user={user}>
      <div className="device-hub">
        <div className="device-hub__head">
          <h2>Device Hub</h2>
          <div className="device-hub__actions">
            <select value={filter} onChange={e => setFilter(e.target.value)}
              style={{ padding: '7px 10px', borderRadius: 8, border: '1px solid #ddd', fontSize: 12 }}>
              <option value="all">All Devices</option>
              <option value="online">Online Only</option>
              <option value="offline">Offline Only</option>
            </select>
            <button className="device-hub__btn" onClick={fetchData} disabled={loading}>
              <RefreshIcon style={{ fontSize: 16 }} /> {loading ? 'Loading...' : 'Refresh'}
            </button>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="dh-stat-grid">
          <StatBox icon={<DevicesOtherIcon />} label="Total Devices" value={data?.summary?.total || 0} color="#551FFF" />
          <StatBox icon={<CheckCircleIcon />} label="Online" value={data?.summary?.online || 0} color="#22c55e" />
          <StatBox icon={<WifiOffIcon />} label="Offline" value={data?.summary?.offline || 0} color="#ef4444" />
          <StatBox icon={<VpnLockIcon />} label="WG Connected" value={data?.summary?.wgConnected || 0} color="#3b82f6" />
          <StatBox icon={<PendingActionsIcon />} label="Pending Cmds" value={data?.summary?.totalPending || 0} color="#f59e0b" />
        </div>

        {/* Device Table */}
        <div className="dh-table-wrap">
          <table className="dh-table">
            <thead>
              <tr>
                <th>Device</th>
                <th>Store</th>
                <th>Status</th>
                <th>App</th>
                <th>Telemetry</th>
                <th>Last Seen</th>
                <th>Pending</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredDevices.map(d => (
                <tr key={d.id}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{d.name}</div>
                    <div style={{ fontSize: 11, color: '#999' }}><Link to={`/devices/${d.deviceCode}`}>{d.deviceCode}</Link></div>
                    {d.hwMac && <div style={{ fontSize: 10, color: '#bbb' }}>{d.hwMac}</div>}
                  </td>
                  <td>{d.store?.name || '-'}</td>
                  <td>
                    {statusBadge(d.status.socket)}
                    {' '}{wgBadge(d.status.wg)}
                  </td>
                  <td>{d.appVersion || '-'}</td>
                  <td>
                    {d.telemetry ? (
                      <div className="dh-telemetry">
                        <span title="Player">{d.telemetry.playerState || 'idle'}</span>
                        {d.telemetry.freeStorage != null && <span title="Storage">💾{d.telemetry.freeStorage}MB</span>}
                        {d.telemetry.freeRam != null && <span title="RAM">🧠{d.telemetry.freeRam}MB</span>}
                        {d.telemetry.wifiSignal != null && <span title="WiFi">📶{d.telemetry.wifiSignal}dBm</span>}
                      </div>
                    ) : <span style={{ color: '#ccc', fontSize: 11 }}>No data</span>}
                  </td>
                  <td style={{ fontSize: 12, color: '#888' }}>{fmtTime(d.lastSeen)}</td>
                  <td>
                    {d.pendingCommands > 0
                      ? <span className="dh-badge dh-badge--pending">{d.pendingCommands}</span>
                      : <span style={{ color: '#ccc' }}>0</span>}
                  </td>
                  <td>
                    <div className="dh-action-row">
                      <button className="dh-action-btn" title="Screenshot"
                        onClick={() => quickCmd(d.deviceCode, 'request_screenshot')}>
                        <CameraAltIcon style={{ fontSize: 14 }} />
                      </button>
                      <button className="dh-action-btn" title="Clear Cache"
                        onClick={() => quickCmd(d.deviceCode, 'clear_cache')}>
                        <DeleteSweepIcon style={{ fontSize: 14 }} />
                      </button>
                      <button className="dh-action-btn" title="Restart App"
                        onClick={() => quickCmd(d.deviceCode, 'restart_app')}>
                        <RestartAltIcon style={{ fontSize: 14 }} />
                      </button>
                      <button className="dh-action-btn" title="Send Command"
                        onClick={() => setCmdDialog({ deviceCode: d.deviceCode, name: d.name })}>
                        <SendIcon style={{ fontSize: 14 }} />
                      </button>
                      <button className="dh-action-btn" title="Command History"
                        onClick={() => loadHistory(d.deviceCode)}>
                        <HistoryIcon style={{ fontSize: 14 }} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredDevices.length === 0 && (
                <tr><td colSpan={8} style={{ textAlign: 'center', padding: 30, color: '#999' }}>
                  {loading ? 'Loading...' : 'No devices found'}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Command History Panel */}
        {history && (
          <div className="dh-history-panel">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3><HistoryIcon style={{ fontSize: 18, verticalAlign: 'middle', marginRight: 6 }} />
                Command History — {history.deviceCode}
              </h3>
              <button className="dh-action-btn" onClick={() => setHistory(null)}>
                <CloseIcon style={{ fontSize: 14 }} /> Close
              </button>
            </div>
            <ul className="dh-history-list">
              {history.commands.length === 0 && (
                <li className="dh-history-item" style={{ color: '#999' }}>No command history</li>
              )}
              {history.commands.map((cmd, i) => (
                <li key={i} className="dh-history-item">
                  <span className="dh-history-time">{fmtTime(cmd.createdAt)}</span>
                  <span className="dh-history-cmd">{cmd.command}</span>
                  {cmdStatusBadge(cmd.status)}
                  <span className="dh-history-target" style={{ marginLeft: 'auto' }}>by {cmd.createdBy}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Send Command Dialog */}
        {cmdDialog && (
          <div className="dh-dialog-overlay" onClick={() => setCmdDialog(null)}>
            <div className="dh-dialog" onClick={e => e.stopPropagation()}>
              <h3><TerminalIcon style={{ fontSize: 18, verticalAlign: 'middle', marginRight: 6 }} />
                Send Command — {cmdDialog.name}
              </h3>
              <label>Command</label>
              <select value={cmdType} onChange={e => setCmdType(e.target.value)}>
                {COMMANDS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
              <label>Device Code</label>
              <input readOnly value={cmdDialog.deviceCode} style={{ background: '#f5f5f5' }} />
              <div className="dh-dialog__actions">
                <button className="device-hub__btn device-hub__btn--secondary" onClick={() => setCmdDialog(null)}>Cancel</button>
                <button className="device-hub__btn" onClick={sendCommand} disabled={sending}>
                  <SendIcon style={{ fontSize: 14 }} /> {sending ? 'Sending...' : 'Send'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </MaybeLayout>
  );
}

export default DeviceHub;
